import {
  SENSITIVITY_UNCLASSIFIED_ID,
  TOP_DATA_TAB_IDS,
  type TopDataTabId,
} from '@dawn/feature-config/widgets';
import { TopDataAccessedBody, type TopDataAccessedTab, type TopDataAccessedTabRow } from '@dawn/ui';

import type { WidgetConfig } from '@/config/dashboard.types';
import { useDrillToAccessDiscovery } from '@/hooks/use-drill-to-access-discovery';
import { USER_TYPE_BY_KIND, type AccessDiscoveryFileLink } from '@/lib/access-legitimacy-scope';

import { WidgetShell } from '../WidgetShell';

/**
 * Inline mirror of the dashboard API's TopDataAccessedResponse shape.
 * Kept local to honor the "no shared contract package" deferral
 * (see TDEV-168 design.md § Decision Log #8).
 *
 * `drilldown` stays here, not on the body's rows: the widget turns a row or
 * identity-count click into the file's events-grid link (TDEV-1116).
 */
interface ResponseEvents {
  read: number;
  write: number;
  download: number;
  delete: number;
}

interface ResponseRow {
  id: string;
  fileName: string;
  documentTopic: string;
  sensitivityId: string;
  sensitivityLabel: string;
  accessEvents: number;
  events: ResponseEvents;
  distinctHICount: number;
  distinctNHICount: number;
  drilldown?: { kind: string; fileId: string; source?: string };
  isHighlighted?: boolean;
}

interface ResponseTab {
  id: TopDataTabId | typeof SENSITIVITY_UNCLASSIFIED_ID;
  label: string;
  count: number;
  rows: ResponseRow[];
}

interface ResponseData {
  tabs: ResponseTab[];
  /** Absent on an API older than this bundle; the card is then six tabs. */
  unclassifiedTab?: ResponseTab;
  /** The card's cutoff; a drill ends the grid there. Absent on an older API. */
  asOf?: string;
}

const REQUIRED_TAB_IDS = new Set<string>(TOP_DATA_TAB_IDS);

function narrowEvents(value: unknown): ResponseEvents | null {
  if (value === null || typeof value !== 'object') return null;
  const e = value as Record<string, unknown>;
  if (typeof e.read !== 'number') return null;
  if (typeof e.write !== 'number') return null;
  if (typeof e.download !== 'number') return null;
  if (typeof e.delete !== 'number') return null;
  return e as unknown as ResponseEvents;
}

function narrowRow(value: unknown): ResponseRow | null {
  if (value === null || typeof value !== 'object') return null;
  const r = value as Record<string, unknown>;
  if (typeof r.id !== 'string') return null;
  if (typeof r.fileName !== 'string') return null;
  if (typeof r.documentTopic !== 'string') return null;
  if (typeof r.sensitivityId !== 'string') return null;
  if (typeof r.sensitivityLabel !== 'string') return null;
  if (typeof r.accessEvents !== 'number') return null;
  if (narrowEvents(r.events) === null) return null;
  if (typeof r.distinctHICount !== 'number') return null;
  if (typeof r.distinctNHICount !== 'number') return null;
  return r as unknown as ResponseRow;
}

const UNCLASSIFIED_TAB_ID = new Set<string>([SENSITIVITY_UNCLASSIFIED_ID]);

function narrowTab(value: unknown, allowedIds: ReadonlySet<string>): ResponseTab | null {
  if (value === null || typeof value !== 'object') return null;
  const t = value as Record<string, unknown>;
  if (typeof t.id !== 'string' || !allowedIds.has(t.id)) return null;
  if (typeof t.label !== 'string') return null;
  if (typeof t.count !== 'number') return null;
  if (!Array.isArray(t.rows)) return null;
  for (const row of t.rows as unknown[]) {
    if (narrowRow(row) === null) return null;
  }
  return t as unknown as ResponseTab;
}

export function narrowTopDataAccessedData(input: unknown): ResponseData | null {
  if (input === null || typeof input !== 'object') return null;
  const candidate = input as Record<string, unknown>;
  if (!Array.isArray(candidate.tabs)) return null;
  if (candidate.tabs.length !== REQUIRED_TAB_IDS.size) return null;

  const seenIds = new Set<string>();
  for (const tab of candidate.tabs as unknown[]) {
    if (narrowTab(tab, REQUIRED_TAB_IDS) === null) return null;
    seenIds.add((tab as Record<string, unknown>).id as string);
  }
  if (seenIds.size !== REQUIRED_TAB_IDS.size) return null;

  // Beside `tabs`, not inside it, so an API that does not derive it yet is a
  // six-tab card rather than an error one. Present but malformed is a reject.
  if (
    candidate.unclassifiedTab !== undefined &&
    narrowTab(candidate.unclassifiedTab, UNCLASSIFIED_TAB_ID) === null
  ) {
    return null;
  }
  if (candidate.asOf !== undefined && typeof candidate.asOf !== 'string') return null;
  return candidate as unknown as ResponseData;
}

/**
 * The events-grid file behind a row. A `file_key` names a file only next to its
 * source, so a row without both is not a link.
 */
function fileLinkOf(row: ResponseRow): AccessDiscoveryFileLink | null {
  const { fileId, source } = row.drilldown ?? {};
  if (typeof fileId !== 'string' || fileId.length === 0) return null;
  if (typeof source !== 'string' || source.length === 0) return null;
  return { key: fileId, source, label: row.fileName };
}

export function TopDataAccessedWidget({ config }: { config: WidgetConfig }): JSX.Element {
  const drillToAccessDiscovery = useDrillToAccessDiscovery(config.id);

  return (
    <WidgetShell
      config={config}
      // The body's last row carries `py-3` (12px) — the default WidgetCardShell
      // `pb-5` (20px) layered on top leaves visible bottom whitespace. Tighten
      // to `pb-3` so the card hugs the table per Figma `5:23313`. Keeps left/
      // right/top padding at the WidgetCardShell defaults.
      contentClassName="px-5 pt-6 pb-3"
      renderData={(envelopeData) => {
        const data = narrowTopDataAccessedData(envelopeData);
        if (!data) return null;
        // Every drill ends the grid's window at this card's cutoff.
        const drill = (link: Parameters<typeof drillToAccessDiscovery>[0]) =>
          drillToAccessDiscovery({ ...link, asOf: data.asOf });

        const payloadTabs = data.unclassifiedTab ? [...data.tabs, data.unclassifiedTab] : data.tabs;
        const fileById = new Map<string, AccessDiscoveryFileLink>();
        for (const row of payloadTabs.flatMap((t) => t.rows)) {
          const file = fileLinkOf(row);
          if (file) fileById.set(row.id, file);
        }
        const tabs: TopDataAccessedTab[] = payloadTabs.map((t) => ({
          id: t.id,
          label: t.label,
          count: t.count,
          rows: t.rows.map<TopDataAccessedTabRow>((r) => ({
            id: r.id,
            fileName: r.fileName,
            documentTopic: r.documentTopic,
            sensitivityId: r.sensitivityId,
            sensitivityLabel: r.sensitivityLabel,
            accessEvents: r.accessEvents,
            events: r.events,
            distinctHICount: r.distinctHICount,
            distinctNHICount: r.distinctNHICount,
            isHighlighted: r.isHighlighted,
            isInteractive: fileById.has(r.id),
          })),
        }));

        return (
          <TopDataAccessedBody
            tabs={tabs}
            defaultTabId="all"
            onRowClick={(row) => {
              const file = fileById.get(row.id);
              if (file) drill({ file });
            }}
            onUserTypeClick={(row, kind) => {
              const file = fileById.get(row.id);
              if (file) drill({ file, userTypes: [USER_TYPE_BY_KIND[kind]] });
            }}
          />
        );
      }}
    />
  );
}
